import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLanguage } from '../LanguageContext';
import { useTheme } from '../ThemeContext';
import {
  getBottomRightCornerRect,
  getBottomLeftCornerRect,
  getTileStartRect,
  getZoomTargetFrame,
  interpolateZoomRect,
} from '../hooks/usePortfolioZoomScroll';
import { useViewportGridTiles } from '../hooks/useViewportGridTiles';
import {
  getRectFromDom,
  getTileRect,
  tileCollidesWithWords,
} from '../utils/gridCollision';
import {
  GRID_MANIFEST,
  GRID_MANIFEST_FONT_MAX,
  GRID_MANIFEST_FONT_MIN,
  GRID_MANIFEST_FONT_PREFERRED,
  GRID_WORD_COLLISION_PADDING_PX,
  GRID_WORD_COLLISION_PADDING_PX_MD,
  TILE_RADIUS,
  ZOOM_CAPTION,
  BUILD_TEXT,
  MIDDLE_ZOOM_TEXT,
  CAPTION_RETURN_FADE_MULTIPLIER,
  getSecondZoomTileIndex,
  getThirdZoomTileIndex,
  getGridSectionFrame,
  getDesktopZoomImageFrame,
  getPrimaryZoomImageDimensions,
  clampZoomImageLeft,
  MIDDLE_ZOOM_IMAGE_ASPECT_RATIO,
  getSecondZoomSource,
  getThirdZoomSource,
  getTileZoomSource,
  portfolioZoomHandoffRects,
  setPortfolioZoomHandoffRects,
} from '../portfolioGridData';

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function smoothstep(edge0, edge1, value) {
  if (value <= edge0) {
    return 0;
  }

  if (value >= edge1) {
    return 1;
  }

  const t = (value - edge0) / (edge1 - edge0);

  return t * t * (3 - 2 * t);
}

function readViewportRect(node) {
  if (!node) {
    return null;
  }

  const { left, top, width, height } = node.getBoundingClientRect();

  return { left, top, width, height };
}

/**
 * @param {DOMRect} textRect
 * @param {Array<{ left: number; top: number; width: number; height: number } | null>} imageRects
 */
function getCaptionImageClipPath(textRect, imageRects) {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let hasIntersection = false;

  imageRects.forEach((rect) => {
    if (!rect) {
      return;
    }

    const x1 = Math.max(textRect.left, rect.left);
    const y1 = Math.max(textRect.top, rect.top);
    const x2 = Math.min(textRect.right, rect.left + rect.width);
    const y2 = Math.min(textRect.bottom, rect.top + rect.height);

    if (x2 <= x1 || y2 <= y1) {
      return;
    }

    hasIntersection = true;
    minX = Math.min(minX, x1);
    minY = Math.min(minY, y1);
    maxX = Math.max(maxX, x2);
    maxY = Math.max(maxY, y2);
  });

  if (!hasIntersection) {
    return null;
  }

  return `inset(${minY - textRect.top}px ${textRect.right - maxX}px ${textRect.bottom - maxY}px ${minX - textRect.left}px)`;
}

function rectsEqual(a, b) {
  if (a === b) {
    return true;
  }

  if (!a || !b) {
    return false;
  }

  return (
    a.left === b.left &&
    a.top === b.top &&
    a.width === b.width &&
    a.height === b.height
  );
}

function PortfolioGridTile({ tile, tileWidth, tileHeight, gridColumn, gridRow, opacity, tileRef }) {
  return (
    <div
      ref={tileRef}
      style={{
        gridColumn,
        gridRow,
        width: `${tileWidth}px`,
        height: `${tileHeight}px`,
        borderRadius: TILE_RADIUS,
        justifySelf: 'start',
        alignSelf: 'start',
        opacity,
      }}
      className="relative overflow-hidden shadow-[0_16px_40px_-18px_rgba(0,0,0,0.55)] ring-1 ring-white/10"
    >
      <img
        src={tile.thumbnail}
        alt=""
        loading="lazy"
        decoding="async"
        className="absolute inset-0 h-full w-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-black/15" />
    </div>
  );
}

function ZoomImageOverlay({ src, rect, opacity = 1, transform, objectFit = 'cover', framed = true }) {
  return (
    <div
      className={
        framed
          ? 'pointer-events-none fixed z-[60] overflow-hidden shadow-[0_24px_60px_-20px_rgba(0,0,0,0.65)] ring-1 ring-white/10'
          : 'pointer-events-none fixed z-[60] overflow-hidden'
      }
      style={{
        left: `${rect.left}px`,
        top: `${rect.top}px`,
        width: `${rect.width}px`,
        height: `${rect.height}px`,
        borderRadius: framed ? TILE_RADIUS : 0,
        opacity,
        transform,
        transformOrigin: 'center center',
      }}
    >
      <img
        src={src}
        alt=""
        loading="eager"
        decoding="sync"
        fetchPriority="high"
        className="h-full w-full"
        style={{ borderRadius: framed ? TILE_RADIUS : 0, objectFit }}
      />
    </div>
  );
}

function PortfolioGrid({
  isMobile,
  zoom1Progress,
  middleZoomProgress,
  captionProgress,
  zoom2Progress,
  gridReturnProgress,
  reducedMotion,
}) {
  const { language } = useLanguage();
  const theme = useTheme();
  const containerRef = useRef(null);
  const captionTextRef = useRef(null);
  const wordRefs = useRef([]);
  const tileRefs = useRef({});
  const [tileRects, setTileRects] = useState(() => ({
    first: portfolioZoomHandoffRects.first,
    second: portfolioZoomHandoffRects.second,
  }));
  const [wordBlocks, setWordBlocks] = useState([]);
  const [captionImageClipPath, setCaptionImageClipPath] = useState(null);
  const [viewportSize, setViewportSize] = useState(() => ({
    width: typeof window !== 'undefined' ? window.innerWidth : 1280,
    height: typeof window !== 'undefined' ? window.innerHeight : 800,
  }));
  const { cols, rowCount, tileWidth, tileHeight, gap, padding, gridOffsetX, tiles } =
    useViewportGridTiles(isMobile);
  const wordPadding = isMobile ? GRID_WORD_COLLISION_PADDING_PX : GRID_WORD_COLLISION_PADDING_PX_MD;
  const unwindRaw = reducedMotion ? 0 : gridReturnProgress;
  const unwind = 1 - (1 - unwindRaw) ** 2;
  const isReturning = unwindRaw > 0;
  const phase1 = reducedMotion ? 0 : isReturning ? 0 : zoom1Progress;
  const phaseMiddle = reducedMotion ? 0 : isReturning ? 1 : middleZoomProgress;
  const phase2 = reducedMotion ? 0 : isReturning ? 1 : captionProgress;
  const phase3 = reducedMotion ? 0 : isReturning ? 1 - unwind : zoom2Progress;
  const hasMeasuredFirstTile = tileRects.first !== null;
  const activePhase1 = hasMeasuredFirstTile ? phase1 : 0;
  const scrollProgress = isReturning ? phase3 : Math.max(activePhase1, phaseMiddle, phase2, phase3);
  const lastIndex = tiles.length - 1;
  const secondIndex = getSecondZoomTileIndex(cols, rowCount, tiles, isMobile);
  const thirdIndex = getThirdZoomTileIndex(cols, rowCount, tiles, isMobile);
  const firstTile = tiles[lastIndex] ?? null;
  const secondTile = tiles[secondIndex] ?? null;
  const thirdTile = tiles[thirdIndex] ?? null;
  const gridShiftY =
    (isReturning ? 1 - unwind : activePhase1) * viewportSize.height * (isMobile ? 0.55 : 0.5);
  const targetFrame = getZoomTargetFrame(viewportSize.width, viewportSize.height, isMobile);
  const { width: sectionWidth, left: sectionLeft } = getGridSectionFrame(viewportSize.width, isMobile);
  const textColumnLeft = targetFrame.textColumnLeft ?? sectionLeft;
  const textColumnWidth = targetFrame.textColumnWidth ?? sectionWidth;
  const secondCornerStart = getBottomRightCornerRect(
    viewportSize.width,
    viewportSize.height,
    tileWidth,
    tileHeight
  );
  const thirdCornerStart = getBottomLeftCornerRect(
    viewportSize.width,
    viewportSize.height,
    tileWidth,
    tileHeight
  );
  const calculatedFirstStartRect = getTileStartRect(
    cols,
    rowCount,
    tileWidth,
    tileHeight,
    gap,
    gridOffsetX,
    viewportSize.height,
    lastIndex
  );
  const calculatedSecondStartRect = getTileStartRect(
    cols,
    rowCount,
    tileWidth,
    tileHeight,
    gap,
    gridOffsetX,
    viewportSize.height,
    secondIndex
  );
  const firstStartRect = tileRects.first ?? calculatedFirstStartRect;
  const secondTileStartRect = tileRects.second ?? calculatedSecondStartRect;

  const manifestWords = useMemo(
    () => GRID_MANIFEST[language].split('\n').filter(Boolean),
    [language]
  );

  const buildTextLines = useMemo(
    () => BUILD_TEXT[language].split('\n').filter(Boolean),
    [language]
  );

  const captionLines = useMemo(
    () => ZOOM_CAPTION[language].split('\n').filter(Boolean),
    [language]
  );

  const middleTextLines = useMemo(
    () => MIDDLE_ZOOM_TEXT[language].split('\n').filter(Boolean),
    [language]
  );

  const buildTextFontPx = Math.min(
    Math.max(viewportSize.width * (isMobile ? 0.087 : 0.055), isMobile ? 39.6 : 42),
    isMobile ? 90 : 76
  );
  const buildTextBlockHeight = buildTextLines.length * buildTextFontPx * 0.92;
  const buildTextTop = isMobile
    ? Math.max(69, targetFrame.headerSpace * 0.345)
    : targetFrame.targetTop + (targetFrame.targetHeight - buildTextBlockHeight) / 2;
  const zoomTarget = useMemo(() => {
    if (isMobile) {
      return { ...targetFrame, targetTop: buildTextTop + buildTextBlockHeight + 10 };
    }

    return targetFrame;
  }, [targetFrame, buildTextTop, buildTextBlockHeight, isMobile]);

  const firstZoomRect = useMemo(() => {
    if (!firstTile || activePhase1 <= 0) {
      return null;
    }

    return interpolateZoomRect(firstStartRect, zoomTarget, activePhase1);
  }, [firstTile, activePhase1, firstStartRect, zoomTarget]);

  const middleTextFontPx = Math.min(
    Math.max(viewportSize.width * (isMobile ? 0.087 : 0.055), isMobile ? 39.6 : 42),
    isMobile ? 90 : 76
  );
  const middleTextBlockHeight = middleTextLines.length * middleTextFontPx * 0.9;
  const middleTarget = useMemo(() => {
    if (isMobile) {
      const baseWidth = sectionWidth;
      const baseHeight = baseWidth / MIDDLE_ZOOM_IMAGE_ASPECT_RATIO;
      const { targetWidth, targetHeight } = getPrimaryZoomImageDimensions(
        baseWidth,
        baseHeight,
        viewportSize.width,
        viewportSize.height
      );

      return {
        targetLeft: clampZoomImageLeft(
          sectionLeft + (baseWidth - targetWidth) / 2,
          targetWidth,
          viewportSize.width
        ),
        targetTop: (viewportSize.height - targetHeight) / 2,
        targetWidth,
        targetHeight,
      };
    }

    const frame = getDesktopZoomImageFrame(
      sectionWidth,
      sectionLeft,
      viewportSize.width,
      viewportSize.height,
      MIDDLE_ZOOM_IMAGE_ASPECT_RATIO
    );

    return {
      targetLeft: frame.targetLeft,
      targetTop: frame.targetTop,
      targetWidth: frame.targetWidth,
      targetHeight: frame.targetHeight,
    };
  }, [isMobile, sectionLeft, sectionWidth, viewportSize.width, viewportSize.height]);

  const thirdZoomRect = useMemo(() => {
    if (!thirdTile || phaseMiddle <= 0) {
      return null;
    }

    const middleZoomAnimProgress = smoothstep(0.1, 0.92, phaseMiddle);

    if (middleZoomAnimProgress <= 0) {
      return null;
    }

    return interpolateZoomRect(thirdCornerStart, middleTarget, middleZoomAnimProgress);
  }, [thirdTile, phaseMiddle, thirdCornerStart, middleTarget]);

  const secondZoomRect = useMemo(() => {
    if (!secondTile) {
      return null;
    }

    if (isReturning) {
      if (unwind >= 1) {
        return null;
      }

      const fullscreen = {
        left: 0,
        top: 0,
        width: viewportSize.width,
        height: viewportSize.height,
      };

      return {
        left: fullscreen.left + (secondTileStartRect.left - fullscreen.left) * unwind,
        top: fullscreen.top + (secondTileStartRect.top - fullscreen.top) * unwind,
        width: fullscreen.width + (secondTileStartRect.width - fullscreen.width) * unwind,
        height: fullscreen.height + (secondTileStartRect.height - fullscreen.height) * unwind,
      };
    }

    if (phase3 <= 0) {
      return null;
    }

    const captionFontPx = Math.min(
      Math.max(viewportSize.width * (isMobile ? 0.1 : 0.06), isMobile ? 44 : 48),
      isMobile ? 100 : 84
    );
    const captionBlockHeight = captionLines.length * captionFontPx * 0.88;
    const baseCaptionTop = zoomTarget.targetTop + zoomTarget.targetHeight + 16;
    const secondImageHeight = Math.min(
      viewportSize.height * (isMobile ? 0.42 : 0.46),
      sectionWidth / (4 / 3)
    );
    const phase3PushY = phase3 * (secondImageHeight + 40);
    const captionY = baseCaptionTop - phase3PushY;
    const layoutTarget = {
      targetLeft: sectionLeft,
      targetTop: captionY + captionBlockHeight + 16,
      targetWidth: sectionWidth,
      targetHeight: secondImageHeight,
    };
    const layoutProgress = clamp(phase3 / 0.75, 0, 1);
    const layoutRect = interpolateZoomRect(secondCornerStart, layoutTarget, layoutProgress);

    if (phase3 <= 0.75) {
      return layoutRect;
    }

    const fullProgress = clamp((phase3 - 0.75) / 0.25, 0, 1);

    return {
      left: layoutRect.left + (0 - layoutRect.left) * fullProgress,
      top: layoutRect.top + (0 - layoutRect.top) * fullProgress,
      width: layoutRect.width + (viewportSize.width - layoutRect.width) * fullProgress,
      height: layoutRect.height + (viewportSize.height - layoutRect.height) * fullProgress,
    };
  }, [
    secondTile,
    phase3,
    isReturning,
    unwind,
    secondCornerStart,
    secondTileStartRect,
    zoomTarget,
    viewportSize,
    captionLines.length,
    isMobile,
    sectionLeft,
    sectionWidth,
  ]);

  const widthProgress =
    firstZoomRect && zoomTarget.targetWidth > firstStartRect.width
      ? clamp(
          (firstZoomRect.width - firstStartRect.width) /
            (zoomTarget.targetWidth - firstStartRect.width),
          0,
          1
        )
      : 0;

  const buildTextProgress =
    phase1 >= 0.82 ? clamp((phase1 - 0.82) / 0.18, 0, 1) : widthProgress >= 0.98 ? 1 : 0;
  const buildTextEase = 1 - (1 - buildTextProgress) ** 3;
  const firstToMiddleCrossfade = smoothstep(0.28, 0.82, phaseMiddle);
  const buildTextFadeOpacity =
    phaseMiddle <= 0.18 ? 1 : clamp(1 - (phaseMiddle - 0.18) / 0.42, 0, 1);
  const showBuildText = buildTextProgress > 0 && !isReturning && buildTextFadeOpacity > 0.02;
  const middleTextProgress =
    phaseMiddle >= 0.55 ? clamp((phaseMiddle - 0.55) / 0.35, 0, 1) : 0;
  const middleTextEase = 1 - (1 - middleTextProgress) ** 3;
  const showMiddleText = middleTextProgress > 0 && !isReturning;
  const middleTextTop = isMobile
    ? viewportSize.height * 0.14
    : middleTarget.targetTop + (middleTarget.targetHeight - middleTextBlockHeight) / 2;
  const middleTextSlideY = (1 - middleTextEase) * -middleTextTop;

  let gridContentOpacity = 1;

  if (showBuildText) {
    gridContentOpacity = 0;
  } else if (isReturning) {
    gridContentOpacity = clamp((unwind - 0.05) / 0.35, 0, 1);
  } else if (activePhase1 > 0) {
    gridContentOpacity = clamp(1 - activePhase1 / 0.82, 0, 1);
  }

  const baseCaptionTop = zoomTarget.targetTop + zoomTarget.targetHeight + 16;
  const secondImageHeight = Math.min(
    viewportSize.height * (isMobile ? 0.42 : 0.46),
    sectionWidth / (4 / 3)
  );
  const phase3PushY = isReturning ? secondImageHeight + 40 : phase3 * (secondImageHeight + 40);
  const captionY = baseCaptionTop - phase3PushY;
  const captionSlideX = (1 - phase2) * -viewportSize.width * 0.4;
  const captionOpacity = isReturning
    ? clamp(1 - unwind * CAPTION_RETURN_FADE_MULTIPLIER, 0, 1)
    : 1;
  const middleTextOpacity = phase3 > 0 ? clamp(1 - phase3 * 1.1, 0, 1) : 1;
  const firstImageOpacity = 1 - firstToMiddleCrossfade;
  const firstImageLiftY = firstToMiddleCrossfade * -52;
  const firstImageScale = 1 - firstToMiddleCrossfade * 0.07;
  const middleImageCrossfadeOpacity = firstToMiddleCrossfade;
  const showFirstImage =
    !isReturning && firstZoomRect && firstTile && firstImageOpacity > 0.02;
  const showMiddleImage =
    !isReturning &&
    thirdZoomRect &&
    thirdTile &&
    middleImageCrossfadeOpacity > 0.03 &&
    phase3 < 0.98;
  const middleImageOpacity =
    middleImageCrossfadeOpacity * (phase3 > 0 ? clamp(1 - phase3 * 1.15, 0, 1) : 1);

  const measureTileRects = () => {
    const firstNode = firstTile ? tileRefs.current[firstTile.id] : null;
    const secondNode = secondTile ? tileRefs.current[secondTile.id] : null;
    const firstRect = readViewportRect(firstNode);
    const secondRect = readViewportRect(secondNode);

    setTileRects((previous) => {
      const nextFirst = firstRect && scrollProgress <= 0.02 ? firstRect : previous.first;
      const nextSecond = secondRect ?? previous.second;

      if (rectsEqual(previous.first, nextFirst) && rectsEqual(previous.second, nextSecond)) {
        return previous;
      }

      setPortfolioZoomHandoffRects(nextFirst, nextSecond);

      return {
        first: nextFirst,
        second: nextSecond,
      };
    });
  };

  const measureWordBlocks = () => {
    const container = containerRef.current;

    if (!container) {
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const blocks = wordRefs.current
      .filter(Boolean)
      .map((node) => getRectFromDom(node.getBoundingClientRect(), containerRect, wordPadding));

    setWordBlocks((previous) => {
      if (
        previous.length === blocks.length &&
        previous.every(
          (block, index) =>
            block.left === blocks[index].left &&
            block.top === blocks[index].top &&
            block.width === blocks[index].width &&
            block.height === blocks[index].height
        )
      ) {
        return previous;
      }

      return blocks;
    });
  };

  const measureCaptionClip = () => {
    const node = captionTextRef.current;

    if (!node || phase2 <= 0 || captionOpacity <= 0.01) {
      setCaptionImageClipPath((previous) => (previous === null ? previous : null));
      return;
    }

    const textRect = node.getBoundingClientRect();
    const clipImages = [];

    if (firstZoomRect && showFirstImage) {
      clipImages.push({ ...firstZoomRect, top: firstZoomRect.top - phase3PushY });
    }

    if (phase3 > 0 && secondZoomRect) {
      clipImages.push(secondZoomRect);
    }

    const nextClipPath = getCaptionImageClipPath(textRect, clipImages);
    setCaptionImageClipPath((previous) => (previous === nextClipPath ? previous : nextClipPath));
  };

  useLayoutEffect(() => {
    measureCaptionClip();
    // measureCaptionClip is intentionally omitted — deps list the values it reads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    phase2,
    captionOpacity,
    captionY,
    captionSlideX,
    phase3,
    firstZoomRect,
    secondZoomRect,
    showFirstImage,
    phase3PushY,
    captionLines,
    language,
    viewportSize.width,
    viewportSize.height,
  ]);

  useLayoutEffect(() => {
    wordRefs.current = wordRefs.current.slice(0, manifestWords.length);
    measureTileRects();
    measureWordBlocks();
    measureCaptionClip();
    // measureTileRects, measureWordBlocks, measureCaptionClip intentionally omitted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    manifestWords,
    wordPadding,
    tileWidth,
    tileHeight,
    rowCount,
    cols,
    gridShiftY,
    firstTile?.id,
    secondTile?.id,
    thirdTile?.id,
    scrollProgress,
    zoom1Progress,
    middleZoomProgress,
  ]);

  useLayoutEffect(() => {
    const container = containerRef.current;

    if (!container || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const observer = new ResizeObserver(() => {
      setViewportSize({ width: window.innerWidth, height: window.innerHeight });
      measureTileRects();
      measureWordBlocks();
      measureCaptionClip();
    });

    observer.observe(container);
    wordRefs.current.filter(Boolean).forEach((node) => observer.observe(node));

    const captionNode = captionTextRef.current;

    if (captionNode) {
      observer.observe(captionNode);
    }

    return () => observer.disconnect();
    // measureTileRects, measureWordBlocks, measureCaptionClip intentionally omitted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manifestWords, wordPadding, gridShiftY, phase2, captionOpacity]);

  const tileMetrics = { gridOffsetX, padding, gap, tileWidth, tileHeight };

  const hiddenTileIds = new Set();

  if (!isReturning && activePhase1 > 0.01) {
    if (firstTile?.id) {
      hiddenTileIds.add(firstTile.id);
    }

    if (secondTile?.id && phase3 > 0.01) {
      hiddenTileIds.add(secondTile.id);
    }

    if (thirdTile?.id && phaseMiddle > 0.01) {
      hiddenTileIds.add(thirdTile.id);
    }
  } else if (isReturning && phase3 > 0.05 && secondTile?.id) {
    hiddenTileIds.add(secondTile.id);
  }

  return (
    <div className="relative h-full w-full overflow-visible">
      {showBuildText && (
        <div
          className="pointer-events-none fixed z-[70]"
          style={{
            left: `${textColumnLeft}px`,
            width: `${textColumnWidth}px`,
            top: `${buildTextTop - phase3PushY}px`,
            transform: `translateY(${(1 - buildTextEase) * -140}px)`,
            opacity: buildTextFadeOpacity,
          }}
        >
          <h2
            className={`w-full font-mono font-semibold leading-[0.92] tracking-[-0.04em] ${theme.colors.text}`}
            style={{
              fontSize: isMobile
                ? 'clamp(2.475rem, 8.7vw, 5.625rem)'
                : 'clamp(2.625rem, 3.8vw, 4.75rem)',
            }}
          >
            {buildTextLines.map((line, index) => {
              const lineProgress = clamp((buildTextProgress - index * 0.14) / 0.72, 0, 1);
              const lineEase = 1 - (1 - lineProgress) ** 3;

              return (
                <span
                  key={`${line}-${index}`}
                  className="block"
                  style={{ transform: `translateY(${(1 - lineEase) * -72}px)` }}
                >
                  {line}
                </span>
              );
            })}
          </h2>
        </div>
      )}

      {showMiddleText && middleTextOpacity > 0.01 && thirdZoomRect && (
        <div
          className="pointer-events-none fixed z-[68]"
          style={{
            left: `${isMobile ? sectionLeft : textColumnLeft}px`,
            width: `${isMobile ? sectionWidth : textColumnWidth}px`,
            top: `${middleTextTop - phase3PushY}px`,
            transform: `translateY(${middleTextSlideY}px)`,
            opacity: middleTextOpacity,
          }}
        >
          <h2
            className={`font-mono font-semibold leading-[0.9] tracking-[-0.04em] ${theme.colors.text}`}
            style={{
              fontSize: isMobile
                ? 'clamp(2.35rem, 8vw, 5rem)'
                : 'clamp(2.5rem, 3.6vw, 4.5rem)',
            }}
          >
            {middleTextLines.map((line, index) => {
              const lineProgress = clamp((middleTextProgress - index * 0.14) / 0.72, 0, 1);
              const lineEase = 1 - (1 - lineProgress) ** 3;

              return (
                <span
                  key={`${line}-${index}`}
                  className="block"
                  style={{ transform: `translateY(${(1 - lineEase) * -56}px)` }}
                >
                  {line}
                </span>
              );
            })}
          </h2>
        </div>
      )}

      {phase2 > 0 && captionOpacity > 0.01 && (
        <div
          className="pointer-events-none fixed z-[65]"
          style={{
            left: `${sectionLeft}px`,
            width: `${sectionWidth}px`,
            top: `${captionY}px`,
            transform: `translateX(${captionSlideX}px)`,
            opacity: captionOpacity,
          }}
        >
          <div className="relative ml-auto w-fit">
            <p
              ref={captionTextRef}
              className={`text-right font-mono font-semibold leading-[0.88] tracking-[-0.04em] ${theme.colors.text}`}
              style={{
                fontSize: isMobile
                  ? 'clamp(2.75rem, 10vw, 6.25rem)'
                  : 'clamp(2.75rem, 4.2vw, 5.25rem)',
              }}
            >
              {captionLines.map((line, index) => {
                const lineProgress = clamp((phase2 - index * 0.12) / 0.76, 0, 1);
                const lineEase = 1 - (1 - lineProgress) ** 3;

                return (
                  <span
                    key={`${line}-${index}`}
                    className="block"
                    style={{ transform: `translateX(${(1 - lineEase) * -80}px)` }}
                  >
                    {line}
                  </span>
                );
              })}
            </p>
            {captionImageClipPath && (
              <p
                aria-hidden
                className="pointer-events-none absolute inset-0 text-right font-mono font-semibold leading-[0.88] tracking-[-0.04em] text-black"
                style={{
                  fontSize: isMobile
                    ? 'clamp(2.75rem, 10vw, 6.25rem)'
                    : 'clamp(2.75rem, 4.2vw, 5.25rem)',
                  clipPath: captionImageClipPath,
                }}
              >
                {captionLines.map((line, index) => {
                  const lineProgress = clamp((phase2 - index * 0.12) / 0.76, 0, 1);
                  const lineEase = 1 - (1 - lineProgress) ** 3;

                  return (
                    <span
                      key={`clip-${line}-${index}`}
                      className="block"
                      style={{ transform: `translateX(${(1 - lineEase) * -80}px)` }}
                    >
                      {line}
                    </span>
                  );
                })}
              </p>
            )}
          </div>
        </div>
      )}

      <div
        className="relative h-full w-full overflow-visible"
        style={{
          transform: `translateY(${-gridShiftY}px)`,
          opacity: gridContentOpacity,
        }}
      >
        <div
          ref={containerRef}
          className="relative grid h-full w-full content-center overflow-visible"
          style={{
            padding: isMobile ? `${padding}px` : `${padding}px 0`,
            gap: `${gap}px`,
            gridTemplateColumns: `repeat(${cols}, ${tileWidth}px)`,
            gridTemplateRows: `repeat(${rowCount}, ${tileHeight}px)`,
            justifyContent: 'center',
          }}
        >
          <div
            aria-hidden
            className="pointer-events-none absolute left-8 top-1/2 z-10 flex -translate-y-1/2 flex-col items-start md:left-12"
            style={{
              fontSize: `clamp(${GRID_MANIFEST_FONT_MIN}, ${GRID_MANIFEST_FONT_PREFERRED}, ${GRID_MANIFEST_FONT_MAX})`,
            }}
          >
            {manifestWords.map((word, index) => (
              <span
                key={`${word}-${index}`}
                ref={(node) => {
                  wordRefs.current[index] = node;
                }}
                className={`block font-mono font-semibold leading-[0.88] tracking-[-0.045em] ${theme.colors.text}`}
              >
                {word}
              </span>
            ))}
          </div>

          {tiles.map((tile, index) => {
            const col = index % cols;
            const row = Math.floor(index / cols);
            const tileRect = getTileRect(col, row, tileMetrics);
            const opacity = hiddenTileIds.has(tile.id) ? 0 : 1;

            if (tileCollidesWithWords(tileRect, wordBlocks, 1) && !hiddenTileIds.has(tile.id)) {
              return null;
            }

            return (
              <PortfolioGridTile
                key={tile.id}
                tile={tile}
                tileWidth={tileWidth}
                tileHeight={tileHeight}
                gridColumn={col + 1}
                gridRow={row + 1}
                opacity={opacity}
                tileRef={(node) => {
                  tileRefs.current[tile.id] = node;
                }}
              />
            );
          })}
        </div>
      </div>

      {showFirstImage && firstZoomRect && firstTile && (
        <ZoomImageOverlay
          src={getTileZoomSource(firstTile)}
          rect={{
            ...firstZoomRect,
            top: firstZoomRect.top - phase3PushY,
          }}
          opacity={firstImageOpacity}
          transform={`translateY(${firstImageLiftY}px) scale(${firstImageScale})`}
        />
      )}

      {showMiddleImage && thirdZoomRect && thirdTile && (
        <ZoomImageOverlay
          src={getThirdZoomSource()}
          rect={{
            ...thirdZoomRect,
            top: thirdZoomRect.top - phase3PushY,
          }}
          opacity={middleImageOpacity}
          objectFit="cover"
          framed={false}
        />
      )}

      {secondZoomRect && secondTile && phase3 > 0 && (
        <ZoomImageOverlay src={getSecondZoomSource()} rect={secondZoomRect} />
      )}
    </div>
  );
}

export default PortfolioGrid;
